// backoffice/src/redux/slices/givenSamples/givenSamplesSlice.js
import { createSlice } from '@reduxjs/toolkit';
import { 
    getGivenSamplesSummary, 
    getGivenSamplesAllKpis,
    getDelegateSamplePivotData 
} from './givenSamplesThunk';

const initialState = {
  summary: {
    data: [],
    meta: { 
      currentPage: 1,
      itemsPerPage: 10,
      totalItems: 0,
      totalPages: 1, // Initialisé à 1, sera recalculé
      hasNextPage: false,
      hasPreviousPage: false,
    },
    loading: false,
    error: null,
  },
  dashboardKpis: {
    data: {
        totalSamplesDistributed: 0,
        uniqueDoctorsReached: 0,
        uniqueDelegatesActive: 0,
    },
    loading: false,
    error: null,
  },
  delegatePivot: {
    data: [],
    loading: false,
    error: null,
    currentDelegateInfo: null,
  }
};

const givenSamplesSlice = createSlice({
  name: 'givenSamples',
  initialState,
  reducers: {
    clearPivotData: (state) => {
      state.delegatePivot.data = [];
      state.delegatePivot.loading = false;
      state.delegatePivot.error = null;
      state.delegatePivot.currentDelegateInfo = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(getGivenSamplesSummary.pending, (state) => {
        state.summary.loading = true;
        state.summary.error = null;
      })
      .addCase(getGivenSamplesSummary.fulfilled, (state, action) => {
        state.summary.loading = false;
        state.summary.error = null;

        // action.payload est la réponse de l'API: { data: [...], total: 8108, hasNextPage: true, ... }
        // action.meta.arg contient { page, limit, filters, signal }
        
        if (action.payload && Array.isArray(action.payload.data) && action.meta?.arg) {
          state.summary.data = action.payload.data; 
          
          const { page: requestedPage, limit: requestedLimit } = action.meta.arg;
          const totalFromApi = action.payload.total || 0;
          const itemsPerPage = requestedLimit || initialState.summary.meta.itemsPerPage;
          const currentPage = requestedPage || initialState.summary.meta.currentPage;

          state.summary.meta = {
            currentPage: currentPage,
            itemsPerPage: itemsPerPage,
            totalItems: totalFromApi,
            totalPages: Math.ceil(totalFromApi / itemsPerPage) || 1, // Calculer totalPages
            hasNextPage: action.payload.hasNextPage !== undefined 
                            ? action.payload.hasNextPage 
                            : (totalFromApi > currentPage * itemsPerPage), // Calculer si non fourni
            hasPreviousPage: currentPage > 1,
          };
          console.log("DEBUG [Slice] Updated summary.meta:", JSON.stringify(state.summary.meta));
        } else {
          console.error("DEBUG (givenSamplesSlice) - getSummary: Unexpected payload structure or missing request args:", action.payload, action.meta);
          state.summary.data = [];
          state.summary.meta = { ...initialState.summary.meta };
        }
      })
      .addCase(getGivenSamplesSummary.rejected, (state, action) => {
        state.summary.loading = false;
        state.summary.error = action.payload;
        state.summary.data = [];
        state.summary.meta = { ...initialState.summary.meta };
        console.error('DEBUG (givenSamplesSlice) - getSummary.rejected - Error:', action.payload);
      })

      // --- Cas pour getGivenSamplesAllKpis ---
      .addCase(getGivenSamplesAllKpis.pending, (state) => {
        state.dashboardKpis.loading = true;
        state.dashboardKpis.error = null;
      })
      .addCase(getGivenSamplesAllKpis.fulfilled, (state, action) => {
        state.dashboardKpis.loading = false;
        state.dashboardKpis.data = action.payload || initialState.dashboardKpis.data; 
        state.dashboardKpis.error = null;
      })
      .addCase(getGivenSamplesAllKpis.rejected, (state, action) => {
        state.dashboardKpis.loading = false;
        state.dashboardKpis.error = action.payload;
        state.dashboardKpis.data = initialState.dashboardKpis.data;
      })

      // --- Cas pour getDelegateSamplePivotData ---
      .addCase(getDelegateSamplePivotData.pending, (state, action) => {
        state.delegatePivot.loading = true;
        state.delegatePivot.error = null;
        state.delegatePivot.data = []; 
        if (action.meta.arg.delegateInfo) {
          state.delegatePivot.currentDelegateInfo = action.meta.arg.delegateInfo;
        }
      })
      .addCase(getDelegateSamplePivotData.fulfilled, (state, action) => {
        state.delegatePivot.loading = false;
        state.delegatePivot.data = action.payload.pivotData || []; 
        state.delegatePivot.error = null;
        state.delegatePivot.currentDelegateInfo = action.payload.delegateInfoRequest || state.delegatePivot.currentDelegateInfo;
      })
      .addCase(getDelegateSamplePivotData.rejected, (state, action) => {
        state.delegatePivot.loading = false;
        if (action.payload && !action.payload.cancelled) {
            state.delegatePivot.error = action.payload.error || action.payload.message || 'Unknown error fetching pivot data.';
        } else if (!action.payload || !action.payload.cancelled) {
            state.delegatePivot.error = 'Unknown error fetching pivot data.';
        }
        if (action.payload && action.payload.delegateInfoRequest) {
            state.delegatePivot.currentDelegateInfo = action.payload.delegateInfoRequest;
        }
      });
  },
});

export const { clearPivotData } = givenSamplesSlice.actions;
export default givenSamplesSlice.reducer;